import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

export type PortalDraft = {
  indikatorAnswers: Record<string, number>
  reflexionTexts: Record<string, string>
  screenIndex: number
}

const drafts = ref<Record<string, PortalDraft>>({})

const emptyDraft = (): PortalDraft => ({
  indikatorAnswers: {},
  reflexionTexts: {},
  screenIndex: 0,
})

const writeDraft = (surveyId: string, draft: PortalDraft) => {
  drafts.value = {
    ...drafts.value,
    [surveyId]: draft,
  }
}

export const readPortalDraft = (surveyId: string): PortalDraft => {
  return drafts.value[surveyId] ?? emptyDraft()
}

export const usePortalDraft = (surveyId: MaybeRefOrGetter<string>) => {
  const draft = computed(() => drafts.value[toValue(surveyId)] ?? emptyDraft())

  const ensure = () => {
    const id = toValue(surveyId)
    if (!drafts.value[id]) {
      writeDraft(id, emptyDraft())
    }
    return id
  }

  const setIndikatorAnswer = (indikatorId: string, value: number) => {
    const id = ensure()
    const current = drafts.value[id] ?? emptyDraft()
    writeDraft(id, {
      ...current,
      indikatorAnswers: {
        ...current.indikatorAnswers,
        [indikatorId]: value,
      },
    })
  }

  const setReflexionText = (questionId: string, text: string) => {
    const id = ensure()
    const current = drafts.value[id] ?? emptyDraft()
    writeDraft(id, {
      ...current,
      reflexionTexts: {
        ...current.reflexionTexts,
        [questionId]: text,
      },
    })
  }

  const setScreenIndex = (screenIndex: number) => {
    const id = ensure()
    const current = drafts.value[id] ?? emptyDraft()
    writeDraft(id, {
      ...current,
      screenIndex,
    })
  }

  return {
    draft,
    setIndikatorAnswer,
    setReflexionText,
    setScreenIndex,
  }
}

export const clearPortalDraft = (surveyId: string) => {
  if (!(surveyId in drafts.value)) {
    return
  }
  const next = { ...drafts.value }
  delete next[surveyId]
  drafts.value = next
}

export const clearAllPortalDrafts = () => {
  drafts.value = {}
}
